import React, { useState } from 'react';
import { CalendarCheck, Loader2 } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'card',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

const CALENDLY_URL_REGEX = /^https?:\/\/(www\.)?calendly\.com\/.+/i;

// Lazy-load Calendly widget CSS + JS on first interaction (cached via module-level promise)
let calendlyPromise = null;
function loadCalendly() {
  if (calendlyPromise) return calendlyPromise;
  if (window.Calendly) return Promise.resolve();

  calendlyPromise = new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = resolve;
    script.onerror = () => { calendlyPromise = null; reject(new Error('Calendly script failed')); };
    document.head.appendChild(script);
  });

  return calendlyPromise;
}

/**
 * CalendlyRenderer - Render component for calendly block
 * Defers Calendly widget assets until button click for faster page loads.
 */
const CalendlyRenderer = ({ block, theme = {} }) => {
  const { content } = block;
  const { calendlyUrl, buttonText = 'Schedule a Meeting' } = content;

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);
  const [loading, setLoading] = useState(false);

  const isValidUrl = CALENDLY_URL_REGEX.test(calendlyUrl);

  const handleClick = async () => {
    if (!isValidUrl) return;

    setLoading(true);
    try {
      await loadCalendly();
      window.Calendly.initPopupWidget({ url: calendlyUrl });
    } catch {
      window.open(calendlyUrl, '_blank');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className={`max-w-4xl mx-auto flex flex-col ${styles.contentAlignmentClass}`}>
        {isValidUrl ? (
          <button
            onClick={handleClick}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-all hover:opacity-90 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg min-h-[48px] sm:min-h-[52px]"
            style={styles.getButtonStyles()}
          >
            {loading ? <Loader2 size={20} className="animate-spin" /> : <CalendarCheck size={20} />}
            {loading ? 'Loading...' : buttonText}
          </button>
        ) : (
          <p className="text-gray-400 italic text-sm">
            Enter a Calendly URL to show the booking button
          </p>
        )}
      </div>
    </div>
  );
};

export default CalendlyRenderer;
